import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["cms_import"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      cmsImportList: build.query<CmsImportListApiResponse, CmsImportListApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/cms-imports/`,
            params: {
              cms_type: queryArg.cmsType,
              firm_id: queryArg.firmId,
              phase: queryArg.phase,
              status: queryArg.status,
              limit: queryArg.limit,
              offset: queryArg.offset,
            },
          }),
          providesTags: ["cms_import"],
        },
      ),
      cmsImportCreate: build.mutation<
        CmsImportCreateApiResponse,
        CmsImportCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportRetrieve: build.query<
        CmsImportRetrieveApiResponse,
        CmsImportRetrieveApiArg
      >({
        query: (queryArg) => ({ url: `/api/cms-imports/${queryArg.id}/` }),
        providesTags: ["cms_import"],
      }),
      cmsImportBulkUpdateMatches: build.mutation<
        CmsImportBulkUpdateMatchesApiResponse,
        CmsImportBulkUpdateMatchesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/bulk-update-matches/`,
          method: "POST",
          body: queryArg.bulkUpdateCaseMatchesRequestSchema,
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportCases: build.query<
        CmsImportCasesApiResponse,
        CmsImportCasesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/cases/`,
        }),
        providesTags: ["cms_import"],
      }),
      cmsImportCasesMissingMatters: build.query<
        CmsImportCasesMissingMattersApiResponse,
        CmsImportCasesMissingMattersApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/cases-missing-matters/`,
        }),
        providesTags: ["cms_import"],
      }),
      cmsImportDeleteCreatedTasks: build.mutation<
        CmsImportDeleteCreatedTasksApiResponse,
        CmsImportDeleteCreatedTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/delete-created-tasks/`,
          method: "POST",
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportFilterOptions: build.query<
        CmsImportFilterOptionsApiResponse,
        CmsImportFilterOptionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/filter-options/`,
        }),
        providesTags: ["cms_import"],
      }),
      cmsImportIngest: build.mutation<
        CmsImportIngestApiResponse,
        CmsImportIngestApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/ingest/`,
          method: "POST",
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportMatchingProgress: build.query<
        CmsImportMatchingProgressApiResponse,
        CmsImportMatchingProgressApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/matching-progress/`,
        }),
        providesTags: ["cms_import"],
      }),
      cmsImportReplicate: build.mutation<
        CmsImportReplicateApiResponse,
        CmsImportReplicateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/replicate/`,
          method: "POST",
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportTasks: build.query<
        CmsImportTasksApiResponse,
        CmsImportTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/tasks/`,
          params: {
            assignee_internal_ids: queryArg.assigneeInternalIds,
            case_internal_ids: queryArg.caseInternalIds,
            statuses: queryArg.statuses,
          },
        }),
        providesTags: ["cms_import"],
      }),
      cmsImportUpdateCaseMatch: build.mutation<
        CmsImportUpdateCaseMatchApiResponse,
        CmsImportUpdateCaseMatchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/update-case-match/`,
          method: "POST",
          body: queryArg.updateCaseMatchRequestSchema,
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportUpdateFilters: build.mutation<
        CmsImportUpdateFiltersApiResponse,
        CmsImportUpdateFiltersApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/${queryArg.id}/update-filters/`,
          method: "POST",
          body: queryArg.updateFiltersRequestSchema,
        }),
        invalidatesTags: ["cms_import"],
      }),
      cmsImportSyncFromApi: build.mutation<
        CmsImportSyncFromApiApiResponse,
        CmsImportSyncFromApiApiArg
      >({
        query: (queryArg) => ({
          url: `/api/cms-imports/sync-from-api/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["cms_import"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type CmsImportListApiResponse =
  /** status 200 List of imports */ {
    count: number;
    next: string | null;
    previous: string | null;
    results: CmsImportSchema[];
  };
export type CmsImportListApiArg = {
  /** Filter imports by CMS type */
  cmsType?: string;
  /** Filter imports by firm */
  firmId?: string;
  /** Filter imports by phase (uploading, filtering, completed) */
  phase?: string;
  /** Filter imports by status (pending, processing, completed, failed) */
  status?: string;
  /** Number of results per page (enables pagination) */
  limit?: number;
  /** Starting position in the result set */
  offset?: number;
};
export type CmsImportCreateApiResponse =
  /** status 201 Created import */ CmsImportSchema;
export type CmsImportCreateApiArg = {
  body: {
    firm_id: string;
    cms_type: string;
    file: Blob;
  };
};
export type CmsImportRetrieveApiResponse =
  /** status 200 Import details */ CmsImportSchema;
export type CmsImportRetrieveApiArg = {
  id: string;
};
export type CmsImportBulkUpdateMatchesApiResponse = unknown;
export type CmsImportBulkUpdateMatchesApiArg = {
  id: string;
  bulkUpdateCaseMatchesRequestSchema: BulkUpdateCaseMatchesRequestSchema;
};
export type CmsImportCasesApiResponse =
  /** status 200 List of case matches */ CmsImportCaseSchemaWithDetails;
export type CmsImportCasesApiArg = {
  id: string;
};
export type CmsImportCasesMissingMattersApiResponse =
  /** status 200 List of cases missing matter assignments */ CmsImportCaseSchemaWithDetails;
export type CmsImportCasesMissingMattersApiArg = {
  id: string;
};
export type CmsImportDeleteCreatedTasksApiResponse =
  /** status 200 Tasks deleted successfully */ DeleteCreatedTasksResultSchema;
export type CmsImportDeleteCreatedTasksApiArg = {
  id: string;
};
export type CmsImportFilterOptionsApiResponse =
  /** status 200 Available filter options */ CmsImportFilterOptionsSchema;
export type CmsImportFilterOptionsApiArg = {
  id: string;
};
export type CmsImportIngestApiResponse =
  /** status 202 Ingestion completed */ IngestionResult;
export type CmsImportIngestApiArg = {
  id: string;
};
export type CmsImportMatchingProgressApiResponse =
  /** status 200 Matching progress */ CmsMatchingProgressSchema;
export type CmsImportMatchingProgressApiArg = {
  id: string;
};
export type CmsImportReplicateApiResponse =
  /** status 202 Replication started */ ReplicationResult;
export type CmsImportReplicateApiArg = {
  id: string;
};
export type CmsImportTasksApiResponse =
  /** status 200 List of filtered tasks */ CmsImportTaskSchemaWithDetails;
export type CmsImportTasksApiArg = {
  /** Filter by assignee internal IDs */
  assigneeInternalIds?: string[];
  /** Filter by case internal IDs */
  caseInternalIds?: string[];
  id: string;
  /** Filter by task statuses */
  statuses?: string[];
};
export type CmsImportUpdateCaseMatchApiResponse =
  /** status 200 Updated case match */ CmsImportCaseSchema;
export type CmsImportUpdateCaseMatchApiArg = {
  id: string;
  updateCaseMatchRequestSchema: UpdateCaseMatchRequestSchema;
};
export type CmsImportUpdateFiltersApiResponse =
  /** status 200 Updated import */ CmsImportSchema;
export type CmsImportUpdateFiltersApiArg = {
  id: string;
  updateFiltersRequestSchema: UpdateFiltersRequestSchema;
};
export type CmsImportSyncFromApiApiResponse =
  /** status 202 Sync workflow started */ {
    workflow_id?: string;
    message?: string;
  };
export type CmsImportSyncFromApiApiArg = {
  body: {
    firm_id: string;
    cms_type: "filevine" | "clio" | "neos" | "casepeer" | "mycase" | "litify";
  };
};
export type ExternalCmsType =
  | "casepeer"
  | "clio"
  | "filevine"
  | "mycase"
  | "neos"
  | "litify"
  | "smartadvocate";
export type CmsImportedTaskFilterSchema = {
  assignee_internal_ids?: string[] | null;
  case_internal_ids?: string[] | null;
  statuses?: string[] | null;
};
export type CmsImportSchema = {
  id: string | string;
  firm_id: string | string;
  firm_name: string;
  cms_type: ExternalCmsType;
  created_by_id: (string | string) | null;
  created_by_name: string | null;
  file_name: string;
  file_url: string | null;
  status: string;
  phase: string;
  error_message: string | null;
  task_filters: CmsImportedTaskFilterSchema;
  created_at: string;
  modified_at: string;
};
export type BulkUpdateCaseMatchesRequestSchema = {
  corrections: {
    [key: string]: (string | string) | null;
  };
};
export type CmsCaseMatchingStatus = "pending" | "completed" | "failed";
export type CmsCaseMatchType =
  | "link_match"
  | "fuzzy_match"
  | "no_match"
  | "user_override";
export type CmsImportedCaseSchema = {
  internal_id: string | string;
  external_id: string;
  cms_type: ExternalCmsType;
  name: string;
  status?: string | null;
  task_count?: number;
};
export type CmsImportCaseSchemaWithDetails = {
  id: string | string;
  cms_import_id: string | string;
  casepeer_case_id: (string | string) | null;
  clio_matter_id: (string | string) | null;
  filevine_project_id: (string | string) | null;
  mycase_case_id: (string | string) | null;
  smartadvocate_case_id: (string | string) | null;
  neos_case_id: (string | string) | null;
  matter_id: (string | string) | null;
  matter_name: string | null;
  matching_status: CmsCaseMatchingStatus;
  match_type: CmsCaseMatchType;
  match_confidence: number | null;
  match_reason: string | null;
  created_at: string;
  modified_at: string;
  cms_case: CmsImportedCaseSchema;
};
export type DeleteCreatedTasksResultSchema = {
  cms_import_id: string | string;
  deleted_count: number;
};
export type CmsImportedAssigneeSchema = {
  internal_id: string | string;
  external_id: string;
  cms_type: ExternalCmsType;
  name: string;
  email: string | null;
};
export type CmsImportFilterOptionsSchema = {
  assignees: CmsImportedAssigneeSchema[];
  cases: CmsImportedCaseSchema[];
  statuses: string[];
  priorities: string[];
};
export type ExternalTaskSchema = {
  id: string | string;
  matter_id: string | string;
  name: string;
  description: string | null;
  status: string;
  due_date: string | null;
  source: string;
  creator_id: (string | string) | null;
  assignee_id: (string | string) | null;
  filevine_note_id: (string | string) | null;
  casepeer_task_id: (string | string) | null;
  clio_task_id: (string | string) | null;
  litify_task_id: (string | string) | null;
  mycase_task_id: (string | string) | null;
  smartadvocate_task_id: (string | string) | null;
  neos_checklist_item_id: (string | string) | null;
  last_synced_at: string | null;
  external_created_at: string | null;
  created_at: string;
  modified_at: string;
};
export type BulkUpsertExternalTasksResult = {
  created: ExternalTaskSchema[];
  updated: ExternalTaskSchema[];
};
export type IngestionResult = {
  tasks_created: number;
  tasks_updated: number;
  tasks_missing_cases: (string | string)[];
  tasks_missing_matters: (string | string)[];
  tasks_blocked_matters: (string | string)[];
  external_tasks: BulkUpsertExternalTasksResult;
};
export type CmsMatchingProgressSchema = {
  total_cases: number;
  completed_cases: number;
  pending_cases: number;
};
export type ReplicationResult = {
  tasks_replicated: number;
  cases_linked: number;
  tasks_linked: number;
};
export type CmsImportTaskAction = "created" | "updated";
export type CmsImportedTaskSchema = {
  internal_id: string | string;
  external_id: string;
  cms_type: ExternalCmsType;
  title: string;
  internal_assignee_id: (string | string) | null;
  external_assignee_id: string | null;
  assignee_name: string | null;
  internal_case_id: (string | string) | null;
  external_case_id: string | null;
  case_name: string | null;
  due_date: string | null;
  status: string;
  priority: string | null;
  created_at: string | null;
  has_additional_assignees?: boolean;
};
export type CmsImportTaskSchemaWithDetails = {
  id: string | string;
  cms_import_id: string | string;
  casepeer_task_id: (string | string) | null;
  clio_task_id: (string | string) | null;
  filevine_note_id: (string | string) | null;
  mycase_task_id: (string | string) | null;
  smartadvocate_task_id: (string | string) | null;
  neos_checklist_item_id: (string | string) | null;
  external_task_id: (string | string) | null;
  import_action: CmsImportTaskAction;
  cms_task: CmsImportedTaskSchema;
};
export type CmsImportCaseSchema = {
  id: string | string;
  cms_import_id: string | string;
  casepeer_case_id: (string | string) | null;
  clio_matter_id: (string | string) | null;
  filevine_project_id: (string | string) | null;
  mycase_case_id: (string | string) | null;
  smartadvocate_case_id: (string | string) | null;
  neos_case_id: (string | string) | null;
  matter_id: (string | string) | null;
  matter_name: string | null;
  matching_status: CmsCaseMatchingStatus;
  match_type: CmsCaseMatchType;
  match_confidence: number | null;
  match_reason: string | null;
  created_at: string;
  modified_at: string;
};
export type UpdateCaseMatchRequestSchema = {
  cms_import_case_id: string | string;
  matter_id?: (string | string) | null;
};
export type UpdateFiltersRequestSchema = {
  filters: CmsImportedTaskFilterSchema;
};
export const {
  useCmsImportListQuery,
  useCmsImportCreateMutation,
  useCmsImportRetrieveQuery,
  useCmsImportBulkUpdateMatchesMutation,
  useCmsImportCasesQuery,
  useCmsImportCasesMissingMattersQuery,
  useCmsImportDeleteCreatedTasksMutation,
  useCmsImportFilterOptionsQuery,
  useCmsImportIngestMutation,
  useCmsImportMatchingProgressQuery,
  useCmsImportReplicateMutation,
  useCmsImportTasksQuery,
  useCmsImportUpdateCaseMatchMutation,
  useCmsImportUpdateFiltersMutation,
  useCmsImportSyncFromApiMutation,
} = injectedRtkApi;
